/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { foldAccessAPI } from '../services/api';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => onAuthStateChanged(auth, async (user) => {
    setFirebaseUser(user);
    setAdmin(null);

    if (!user) {
      setLoading(false);
      return;
    }

    setError(null);
    try {
      const response = await foldAccessAPI.me();
      setAdmin(response.data.admin);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'This Google account is not approved for The Fold.');
      await signOut(auth);
    } finally {
      setLoading(false);
    }
  }), []);

  const login = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (loginError) {
      if (loginError.code !== 'auth/popup-closed-by-user') {
        setError(loginError.message || 'Google sign-in failed.');
      }
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    try {
      await signOut(auth);
    } finally {
      setAdmin(null);
      setLoading(false);
    }
  };

  const value = useMemo(() => ({
    user: admin,
    firebaseUser,
    admin,
    isOwner: admin?.role === 'owner',
    isAuthenticated: Boolean(admin),
    loading,
    error,
    login,
    logout,
  }), [admin, error, firebaseUser, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
